// src/merch/MobileDrawer.jsx
//
// Phone layout helper: the 3D view + editor fill the screen (no page
// scroll fighting the canvases), and every choice lives in this bottom
// sheet — tabs along the top, drag the handle (or tap a tab) to open.
// Drag the handle or the tabs to any height (it stays put).

import React, { useEffect, useRef, useState } from "react";

const BREAKPOINT = 860;

export function useIsMobile() {
  const q = `(max-width: ${BREAKPOINT}px)`;
  const [m, setM] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, [q]);
  return m;
}

const PEEK = 68;          // handle + tabs only
const MAX_RATIO = 0.86;   // tallest: most of the screen
const OPEN_RATIO = 0.46;  // first open / tap-to-open
const SNAP_PX = 22;       // soft snap, only near the ends
const KEY = "mm_drawer_h";

const viewportH = () => Math.round(window.visualViewport?.height || window.innerHeight || 800);
const maxH = () => Math.max(PEEK, Math.round(viewportH() * MAX_RATIO));
const clampH = (h) => Math.max(PEEK, Math.min(maxH(), h));

/* Bottom sheet that stays wherever you leave it (like Tattune): drag the
   handle OR the tab bar up / down to any height; it only snaps when
   you're close to fully closed or fully open. Tapping the handle toggles
   closed ↔ your last open height. */
export default function MobileDrawer({ tabs, initialTab = "product" }) {
  const [tab, setTab] = useState(initialTab);
  const [h, setH] = useState(() => {
    let saved = NaN;
    try { saved = Number(localStorage.getItem(KEY)); } catch { /* private mode */ }
    return clampH(Number.isFinite(saved) && saved > 0 ? saved : viewportH() * OPEN_RATIO);
  });
  const [dragging, setDragging] = useState(false);
  const lastOpen = useRef(h > PEEK + 2 ? h : viewportH() * OPEN_RATIO);
  const drag = useRef(null);
  const swallowClick = useRef(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (h > PEEK + 2) lastOpen.current = h;
    if (!dragging) { try { localStorage.setItem(KEY, String(Math.round(h))); } catch { /* ignore */ } }
  }, [h, dragging]);

  // keep it on screen when the phone rotates or the browser bars change
  useEffect(() => {
    const on = () => setH((x) => clampH(x));
    window.addEventListener("resize", on);
    window.visualViewport?.addEventListener("resize", on);
    return () => { window.removeEventListener("resize", on); window.visualViewport?.removeEventListener("resize", on); };
  }, []);

  function onDown(e, fromHandle) {
    if (e.button && e.button !== 0) return;
    drag.current = { y: e.clientY, h, moved: false, fromHandle, id: e.pointerId, el: e.currentTarget };
    if (fromHandle) e.currentTarget.setPointerCapture?.(e.pointerId);
  }
  function onMove(e) {
    const d = drag.current;
    if (!d || e.pointerId !== d.id) return;
    const dy = d.y - e.clientY;
    if (!d.moved) {
      if (Math.abs(dy) < (d.fromHandle ? 3 : 8)) return; // tabs: a tap stays a tap
      d.moved = true;
      setDragging(true);
      d.el.setPointerCapture?.(e.pointerId);
    }
    setH(clampH(d.h + dy));
  }
  function onUp(e) {
    const d = drag.current;
    if (!d || e.pointerId !== d.id) return;
    drag.current = null;
    setDragging(false);
    if (!d.moved) {
      if (d.fromHandle) setH(h <= PEEK + 2 ? clampH(lastOpen.current) : PEEK);
      return;
    }
    swallowClick.current = true; // the drag ended on a tab: don't also switch tabs
    setTimeout(() => { swallowClick.current = false; }, 0);
    const top = maxH();
    if (h - PEEK <= SNAP_PX) setH(PEEK);
    else if (top - h <= SNAP_PX) setH(top);
  }

  function pickTab(id) {
    if (swallowClick.current) return;
    setTab(id);
    if (h <= PEEK + 2) setH(clampH(lastOpen.current));
    bodyRef.current?.scrollTo?.({ top: 0 });
  }

  const current = tabs.find((t) => t.id === tab) || tabs[0];
  const closed = h <= PEEK + 2;

  return (
    <div className={`ma-drawer ${dragging ? "dragging" : ""}`} style={{ height: h }}>
      <div
        className="ma-drawer-handle"
        onPointerDown={(e) => onDown(e, true)}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        role="button"
        aria-label={closed ? "Open panel (or drag to resize)" : "Close panel (or drag to resize)"}
      >
        <i />
      </div>
      <div
        className="ma-drawer-tabs"
        role="tablist"
        onPointerDown={(e) => onDown(e, false)}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        {tabs.map((t) => (
          <button key={t.id} role="tab" aria-pressed={t.id === current.id} onClick={() => pickTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="ma-drawer-body" ref={bodyRef}>{current.content}</div>
    </div>
  );
}
